import { FC, useState, MouseEvent } from 'react'
import { FaultMode, ResourceImage, SceneRoom } from '../Types'
import { preventContextMenu } from '../Utils'
import { Container, Row, Col, Nav } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'

import { ReactComponent as HomeSVGIcon } from '../assets/icons/home.svg'
import { ReactComponent as FolderSVGIcon } from '../assets/icons/folder.svg'
import { ReactComponent as HelpSVGIcon } from '../assets/icons/help.svg'
import { ReactComponent as ExitSVGIcon } from '../assets/icons/door.svg'
import { IconButton } from '../styles/Buttons'

import HelpMenu from './HelpMenu'
import FolderMenu from './FolderMenu'
import Confirm from './Confirm'

type Props = {
    faultMode?: FaultMode
    pageTitle: string
    currentSceneRoom?: SceneRoom
    dollarsSpent?: string
    timeSpent?: string
    onConfirmExit?: Function
    onHomeClick?: Function
    isHomeAvailable?: boolean
    simIsLoaded?: boolean
    showDiagram?: (diagram: ResourceImage) => void
}

const NavigationBar: FC<Props> = ({
    faultMode,
    pageTitle,
    currentSceneRoom,
    dollarsSpent,
    timeSpent,
    onConfirmExit,
    onHomeClick,
    isHomeAvailable = true,
    simIsLoaded = false,
    showDiagram
}: Props) => {
    const [confirmExitShow, setConfirmExitShow] = useState(false)
    const { exitConfirmTitle, exitConfirmBody } = getExitDialogValues(faultMode, currentSceneRoom)
    const history = useHistory()
    const [showFolderMenu, setShowFolderMenu] = useState(false)
    const [showHelpMenu, setShowHelpMenu] = useState(false)

    const hideFolder = () => setShowFolderMenu(false)
    const hideHelpMenu = () => setShowHelpMenu(false)


    if (!isHomeAvailable && onHomeClick) throw new Error('onHomeClick prop provided, but Home button is not available')

    const doExit = (e: MouseEvent) => {
        e.preventDefault()
        setConfirmExitShow(false)
        onConfirmExit && onConfirmExit()
    }

    const handleHomeClick = (e: MouseEvent) => {
        e.preventDefault()
        onHomeClick && onHomeClick()
        history.push('/')
    }

    const handleFolderClick = (e: MouseEvent) => {
        e.preventDefault()

        setShowFolderMenu(true)
    }

    const handleHelpClick = (e: MouseEvent) => {
        e.preventDefault()
        setShowHelpMenu(true)
    }

    const handleExitClick = (e: MouseEvent) => {
        e.preventDefault()
        if (!simIsLoaded) return
        setConfirmExitShow(true)
    }

    return (
        <>
            <div className="top-bar" onContextMenu={(e: MouseEvent) => preventContextMenu(e)}>
                <Container fluid={true}>
                    <Row className="align-items-center">
                        <Col sm={{ span: 2 }} className="top-left-nav">
                            <Nav className="flex-nowrap">
                                {
                                    isHomeAvailable &&
                                    (
                                        <IconButton
                                            onClick={handleHomeClick}>
                                            <HomeSVGIcon />
                                        </IconButton>
                                    )
                                }
                            </Nav>
                        </Col>
                        <Col sm={{ span: 5 }}>
                            <h1 className="module-title">
                                {pageTitle}
                            </h1>
                        </Col>
                        <Col>
                            <div className="d-flex align-items-center justify-content-end">
                                {simIsLoaded && currentSceneRoom !== SceneRoom.Hallway
                                    && (
                                        <>
                                            <span className="dollars-spent" /><span className="dollars-amount">{dollarsSpent}</span>
                                            <span className="time-spent" /><span className="time-total">{timeSpent}</span>
                                        </>
                                    )}
                            </div>
                        </Col>
                        <Col sm={{ span: 2 }} className="top-right-nav">
                            <Nav className="justify-content-end">
                                {simIsLoaded && showDiagram && currentSceneRoom !== SceneRoom.Hallway && (
                                    <Nav.Link>
                                        <IconButton
                                            title={'Resource Folder'}
                                            onClick={handleFolderClick}
                                        >
                                            <FolderSVGIcon />

                                        </IconButton>
                                    </Nav.Link>
                                )}
                                {showDiagram ?
                                    (<FolderMenu
                                        visible={showFolderMenu}
                                        hideMenu={hideFolder}
                                        showDiagram={showDiagram}
                                    />)
                                    : <></>
                                }
                                <Nav.Link>
                                    <IconButton
                                        title={'Help'}
                                        onClick={handleHelpClick}
                                    >
                                        <HelpSVGIcon />
                                    </IconButton>
                                </Nav.Link>
                                <HelpMenu
                                    visible={showHelpMenu}
                                    hideMenu={hideHelpMenu}
                                />

                                {simIsLoaded && (
                                    <Nav.Link>
                                        <IconButton
                                            title={exitConfirmTitle}
                                            onClick={handleExitClick}
                                        >
                                            <ExitSVGIcon />
                                        </IconButton>
                                    </Nav.Link>
                                )
                                }
                            </Nav>
                        </Col>
                    </Row>
                </Container>
            </div>
            <Confirm
                title={exitConfirmTitle + '?'}
                body={exitConfirmBody}
                show={confirmExitShow}
                confirmText='Yes'
                cancelText='No'
                onConfirm={doExit}
                onHide={() => setConfirmExitShow(false)}
            />
        </>
    )
}
export default NavigationBar



const getExitDialogValues = (faultMode: FaultMode | undefined, sceneRoom: SceneRoom | undefined) => {
    const isCurrentRoomLab = sceneRoom === SceneRoom.Lab

    let exitConfirmTitle: string = isCurrentRoomLab ? 'Exit Lab' : 'Return to Home page'
    let exitConfirmBody: string = isCurrentRoomLab ? 'Are you sure you want to exit the lab?' : 'Are you sure you want to return to the Home page?'

    if (faultMode === FaultMode.Troubleshoot) {
        exitConfirmTitle = 'Exit Fault'
        exitConfirmBody =
            `<p>Before exiting you must verify that:</p>
                <ul>
                    <li>all aspects of the circuit are working correctly</li>
                    <li>all panels are closed</li>
                    <li>all wires are reconnected, and</li>
                    <li>the meter is disconnected.</li>
                </ul>
            <p>Are you sure you want to leave?</p>`
    }
    return { exitConfirmTitle, exitConfirmBody }
}
